import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  LogOut, MessageSquare, Cake, Users as UsersIcon, ShieldCheck, ArrowRight,
  Edit3, Save, X, Lock,
} from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

const FIELDS = [
  { k: "first_name", l: "Prénom", type: "text" },
  { k: "last_name", l: "Nom", type: "text" },
  { k: "phone", l: "Téléphone", type: "tel" },
  { k: "date_of_birth", l: "Date de naissance", type: "date" },
  { k: "city", l: "Ville", type: "text" },
  { k: "country", l: "Pays", type: "text" },
  { k: "function", l: "Fonction", type: "text" },
  { k: "political_engagement", l: "Engagement politique", type: "text" },
  { k: "associative_engagement", l: "Engagement associatif", type: "text" },
  { k: "availability", l: "Disponibilités", type: "text" },
  { k: "bio", l: "Biographie", type: "textarea" },
];

export default function MemberDashboard() {
  const { user, logout } = useAuth();
  const [me, setMe] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState({});
  const [bdays, setBdays] = useState([]);
  const [convs, setConvs] = useState([]);
  const [groups, setGroups] = useState([]);

  const loadMe = () => api.get("/auth/me").then((r) => { setMe(r.data); setDraft(r.data); });

  useEffect(() => {
    loadMe();
    api.get("/birthdays/upcoming?days=30").then((r) => setBdays(r.data.items || [])).catch(() => {});
    api.get("/conversations").then((r) => setConvs(r.data)).catch(() => {});
    api.get("/groups").then((r) => setGroups(r.data)).catch(() => {});
  }, []);

  const save = async () => {
    const patch = {};
    FIELDS.forEach(({ k }) => {
      if (draft[k] !== undefined) patch[k] = draft[k];
    });
    try {
      await api.put("/members/me/profile", patch);
      toast.success("Profil mis à jour");
      setEditing(false);
      loadMe();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  if (!me) return <div className="p-10 text-center text-foreground/55">Chargement…</div>;

  return (
    <div className="min-h-screen bg-bone" data-testid="member-dashboard">
      {/* Hero */}
      <section className="bg-ink text-white py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-5">
            <div className="w-16 h-16 grid place-items-center bg-gold text-ink font-serif text-2xl rounded-sm">
              {(me.first_name?.[0] || "M").toUpperCase()}{(me.last_name?.[0] || "").toUpperCase()}
            </div>
            <div>
              <div className="eyebrow eyebrow-on-dark">Espace membre</div>
              <h1 className="font-serif text-3xl lg:text-4xl mt-1 leading-tight">Bonjour, {me.first_name}</h1>
              <div className="text-white/70 text-sm mt-1">{me.email}</div>
            </div>
          </div>
          <button onClick={logout} className="btn-gold text-sm" data-testid="member-logout">
            <LogOut className="w-4 h-4" /> Déconnexion
          </button>
        </div>
      </section>

      {/* Quick actions */}
      <section className="py-10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <ActionCard
              to="/membres/messages"
              icon={MessageSquare}
              count={convs.length}
              label="Messagerie chiffrée"
              sub="Conversations sécurisées E2E"
              tone="primary"
              tid="card-messages"
            />
            <ActionCard
              icon={Cake}
              count={bdays.length}
              label="Anniversaires"
              sub={bdays.length > 0 ? `Prochain : ${bdays[0].first_name} dans ${bdays[0].days_until}j` : "30 prochains jours"}
              tone="gold"
              tid="card-birthdays"
            />
            <ActionCard
              icon={UsersIcon}
              count={groups.length}
              label="Groupes de travail"
              sub="Publics et privés"
              tone="muted"
              tid="card-groups"
            />
            <ActionCard
              to="/evenements"
              icon={ShieldCheck}
              label="Événements"
              sub="Croisade Scientifique"
              tone="muted"
              tid="card-events"
            />
          </div>
        </div>
      </section>

      {/* Body : Profile + Birthdays */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 grid lg:grid-cols-12 gap-6">
          {/* PROFILE */}
          <div className="lg:col-span-8 bg-white border border-border rounded-sm p-7" data-testid="profile-section">
            <div className="flex items-center justify-between mb-5">
              <div>
                <div className="eyebrow text-gold-dark">Mon profil</div>
                <h2 className="font-serif text-2xl text-ink mt-1 leading-tight">Informations personnelles</h2>
              </div>
              {!editing ? (
                <button onClick={() => setEditing(true)} className="btn-outline text-sm" data-testid="edit-profile-btn">
                  <Edit3 className="w-3.5 h-3.5" /> Modifier
                </button>
              ) : (
                <div className="flex items-center gap-2">
                  <button onClick={() => { setEditing(false); setDraft(me); }} className="text-foreground/55 hover:text-foreground p-2">
                    <X className="w-4 h-4" />
                  </button>
                  <button onClick={save} className="btn-primary text-sm" data-testid="save-profile-btn">
                    <Save className="w-3.5 h-3.5" /> Enregistrer
                  </button>
                </div>
              )}
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              {FIELDS.map((f) => (
                <div key={f.k} className={f.type === "textarea" ? "sm:col-span-2" : ""}>
                  <div className="text-[0.6rem] uppercase tracking-[0.18em] font-semibold text-foreground/55 mb-1.5">{f.l}</div>
                  {editing ? (
                    f.type === "textarea" ? (
                      <textarea value={draft[f.k] || ""} onChange={(e) => setDraft({ ...draft, [f.k]: e.target.value })}
                                rows={3} data-testid={`profile-${f.k}`}
                                className="w-full border border-border rounded-sm p-2.5 text-sm focus:border-ink outline-none resize-none" />
                    ) : (
                      <input type={f.type} value={draft[f.k] || ""} onChange={(e) => setDraft({ ...draft, [f.k]: e.target.value })}
                             data-testid={`profile-${f.k}`}
                             className="w-full border border-border rounded-sm p-2.5 text-sm focus:border-ink outline-none" />
                    )
                  ) : (
                    <div className="text-sm text-ink mt-1 min-h-[1.5em]">
                      {me[f.k] || <span className="text-foreground/35 italic">Non renseigné</span>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* BIRTHDAYS */}
          <div className="lg:col-span-4 space-y-6">
            <div className="bg-white border border-border rounded-sm p-7" data-testid="birthdays-section">
              <div className="flex items-center gap-2 eyebrow text-gold-dark">
                <Cake className="w-3.5 h-3.5" /> Anniversaires à venir
              </div>
              <h3 className="font-serif text-xl text-ink mt-2 leading-tight">30 prochains jours</h3>
              <div className="mt-5 space-y-3">
                {bdays.length === 0 ? (
                  <div className="text-sm text-foreground/55 italic">Aucun anniversaire dans les 30 prochains jours.</div>
                ) : bdays.map((b) => (
                  <div key={b.user_id} className="flex items-center gap-3 p-3 bg-bone rounded-sm" data-testid={`birthday-${b.user_id}`}>
                    <div className="w-10 h-10 grid place-items-center bg-gold/20 text-gold-dark font-serif text-base rounded-sm shrink-0">
                      🎂
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-ink truncate">{b.first_name} {b.last_name}</div>
                      <div className="text-[0.7rem] text-foreground/60">
                        {b.days_until === 0 ? "Aujourd'hui ! 🎉" :
                         b.days_until === 1 ? "Demain" :
                         `Dans ${b.days_until} jours`} · {b.turning} ans
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-ink text-white p-7 rounded-sm">
              <div className="flex items-center gap-2 eyebrow eyebrow-on-dark">
                <Lock className="w-3.5 h-3.5 text-gold" /> Sécurité E2E
              </div>
              <p className="text-sm text-white/80 mt-3 leading-relaxed">
                Votre messagerie utilise un chiffrement bout-en-bout
                (Curve25519). Vos messages ne peuvent être lus que par
                vous et votre interlocuteur.
              </p>
              <Link to="/membres/messages" className="mt-4 inline-flex items-center gap-1.5 text-gold text-sm font-semibold hover:gap-2 transition-all" data-testid="goto-messages">
                Ouvrir la messagerie <ArrowRight className="w-3.5 h-3.5" />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function ActionCard({ to, icon: Icon, count, label, sub, tone, tid }) {
  const styles = {
    primary: "bg-ink text-white border-ink",
    gold: "bg-gold/10 border-gold/40 text-ink",
    muted: "bg-white border-border text-ink",
  };
  const inner = (
    <div className={`p-5 rounded-sm border ${styles[tone]} h-full flex flex-col`} data-testid={tid}>
      <div className="flex items-start justify-between">
        <Icon className={`w-6 h-6 ${tone === "primary" ? "text-gold" : "text-gold-dark"}`} />
        {count !== undefined && (
          <span className={`text-2xl font-serif ${tone === "primary" ? "text-gold" : "text-ink"}`}>{count}</span>
        )}
      </div>
      <div className="font-serif text-base mt-3">{label}</div>
      <div className={`text-xs mt-1 ${tone === "primary" ? "text-white/70" : "text-foreground/55"}`}>{sub}</div>
    </div>
  );
  return to ? <Link to={to}>{inner}</Link> : <div>{inner}</div>;
}
